import { Dossier, Session, TabMenu } from "../../types";
import DocumentsLayout from "./DocumentsLayout/DocumentsLayout";
import DocumentList from "./DocumentCardsList/DocumentList";
import { groupBy } from "../../helpers";
import { useCallback, useEffect, useState } from "react";
import { getReferenciels, getDossiers } from "../../services";
import { Box, Typography } from "@mui/material";
import { useLocalStorage } from "../../hooks";
import { sessionStorageKey } from "../../consts";

export const qualificationFilterMenuItem = {
    key: "certif",
    name: "Certifications",
};

type DocumentsProps = {
    initialFilterMenuItem?: {
        key: string;
        name?: string;
    };
};

export default function Documents(props?: DocumentsProps) {
    const { initialFilterMenuItem } = props || {};
    const [dossierDocuements, setDossierDocuements] = useState<Dossier[]>([]);

    const filterData = (type: string, data: any) => {
        return data
            .filter((d: any) => d.contractUrl || d.certificationUrl)
            .map((doc: any) => {
                const { id, referenciel } = doc;
                switch (type) {
                    case "contracts":
                        if (!doc.contractUrl) {
                            return;
                        }
                        return {
                            id,
                            referenciel,
                            contractUrl: doc.contractUrl,
                        };
                    case "certif":
                        if (!doc.certificationUrl) {
                            return;
                        }
                        return {
                            id,
                            referenciel,
                            certificationUrl: doc.certificationUrl,
                        };
                    default:
                        return doc;
                }
            })
            .filter((d: any) => d);
    };

    const tabsMenu: TabMenu[] = [
        {
            key: "tous",
            name: "Tous",
        },
        qualificationFilterMenuItem,
        {
            key: "contracts",
            name: "Contrats",
        },
    ];

    const [activeTab, setActiveTab] = useState<string>(
        initialFilterMenuItem ? initialFilterMenuItem.key : tabsMenu[0].key
    );

    const [referenciels, setReferentiels] = useState([]);

    const { storedValue: session } =
        useLocalStorage<Session>(sessionStorageKey);

    const {
        auth: { user: currentUser, currentUserRoleId: userRoleId },
    } = session;

    const referencielsGroupedById = referenciels.length
        ? groupBy(referenciels, (ref: any) => ref.id)
        : null;

    const dataFiltered = dossierDocuements.length
        ? filterData(activeTab, dossierDocuements)
        : [];

    const dossierDocumentsGroupedByRefs =
        dataFiltered.length && referencielsGroupedById
            ? groupBy(
                  dataFiltered.map((doc: any) => {
                      if (!doc.referenciel.id) {
                          doc.referenciel =
                              referencielsGroupedById[doc.referenciel][0];
                      }

                      return doc;
                  }),
                  (doc: any) => doc.referenciel.id
              )
            : null;

    const allDocumentNumber = dataFiltered.filter(
        (x: any) => x.contractUrl && x.certificationUrl
    ).length;

    const fetchDossierDocuments = useCallback(async () => {
        if (!currentUser.id) {
            return;
        }
        const dossierDocsRes = await getDossiers(currentUser.id, {
            userRoleId,
        });
        if (
            !dossierDocsRes.detail ||
            (dossierDocsRes && dossierDocsRes.length)
        ) {
            const refsRes = await getReferenciels(
                dossierDocsRes
                    .filter((d: Dossier) => d.referenciel)
                    .map((d: Dossier) => d.referenciel)
            );
            if (!refsRes.detail) {
                setDossierDocuements(dossierDocsRes);
                setReferentiels(refsRes);
            }
        }
    }, []);

    useEffect(() => {
        fetchDossierDocuments();
    }, [fetchDossierDocuments]);

    return (
        <DocumentsLayout
            headerTitle={"Retrouvez ici tous vos documents personnels"}
            numberOfDocs={
                allDocumentNumber ? allDocumentNumber * 2 : dataFiltered.length
            }
            setActiveTab={setActiveTab}
            activeTab={activeTab}
            tabsMenu={tabsMenu}
        >
            {dossierDocumentsGroupedByRefs ? (
                <DocumentList
                    groupedDocumentListByRefs={dossierDocumentsGroupedByRefs}
                />
            ) : (
                <Box>
                    <Typography
                        sx={{
                            color: "#023",
                            fontFamily: "Roboto",
                            fontSize: "1.125rem",
                            fontStyle: "normal",
                            fontWeight: 500,
                            lineHeight: "normal",
                        }}
                    >
                        {"Vous n’avez aucun document."}
                    </Typography>
                </Box>
            )}
        </DocumentsLayout>
    );
}
